import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Card, Button, Badge, Select } from '../../components/ui/Kit';
import Modal from '../../components/ui/Modal';
import Spinner from '../../components/ui/Spinner';
import { api } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {
  getActiveDirectoryUsers,
  loadUsersDirectory,
  resolveDisplayName,
} from '../../lib/usersDirectory';

const { FiShield } = FiIcons;

const MANAGER_ROLES = new Set(['manager', 'admin', 'super_admin']);

function ExclusivityStatusBadge({ status }) {
  if (status === 'non_exclusive') {
    return <Badge variant="success">Non-Exclusive</Badge>;
  }
  return <Badge variant="primary">Exclusive</Badge>;
}

export default function ExclusivityPanel({
  lead,
  leadId,
  viewer,
  canManage,
  onLeadUpdated,
}) {
  const { addToast } = useToast();
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantUserId, setGrantUserId] = useState('');
  const [grantDays, setGrantDays] = useState('14');
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const isNonExclusive = lead.exclusivity_status === 'non_exclusive';
  const viewerId = viewer?.id;
  const isOwner = viewerId && lead.created_by === viewerId;
  const isExclusiveHolder = viewerId && lead.exclusive_to === viewerId;
  const canRequest =
    isNonExclusive && viewerId && !isOwner && !isExclusiveHolder && !lead.exclusive_to;

  const exclusiveHolderName =
    resolveDisplayName(lead.exclusive_to, lead.exclusive_holder);

  const openGrantModal = async () => {
    setGrantOpen(true);
    setGrantUserId(lead.exclusive_to || '');
    setGrantDays('14');
    setRosterLoading(true);
    try {
      const users = await loadUsersDirectory();
      setRoster(getActiveDirectoryUsers(users));
    } catch (err) {
      addToast({ type: 'error', title: 'Could not load team', message: err.message });
      setRoster([]);
    } finally {
      setRosterLoading(false);
    }
  };

  const handleRequestSubmit = async () => {
    const reason = requestReason.trim();
    if (!reason) {
      addToast({ type: 'error', title: 'Reason required', message: 'Please explain why you need exclusivity.' });
      return;
    }
    setRequestSubmitting(true);
    try {
      await api.exclusivity.requestExclusivity(leadId, reason);
      addToast({ type: 'success', title: 'Request submitted', message: 'A manager will review your request.' });
      setRequestOpen(false);
      setRequestReason('');
    } catch (err) {
      addToast({ type: 'error', title: 'Request failed', message: err.message });
    } finally {
      setRequestSubmitting(false);
    }
  };

  const handleSetStatus = async (status) => {
    setActionLoading(true);
    try {
      await api.exclusivity.setStatus(leadId, status);
      addToast({ type: 'success', title: 'Exclusivity updated' });
      await onLeadUpdated?.();
    } catch (err) {
      addToast({ type: 'error', title: 'Update failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleGrant = async () => {
    const days = parseInt(grantDays, 10);
    if (!grantUserId || !days || days < 1) {
      addToast({ type: 'error', title: 'Invalid grant', message: 'Select an agent and enter timeframe days.' });
      return;
    }
    setActionLoading(true);
    try {
      await api.exclusivity.grantExclusivity(leadId, grantUserId, days);
      addToast({ type: 'success', title: 'Exclusivity granted' });
      setGrantOpen(false);
      await onLeadUpdated?.();
    } catch (err) {
      addToast({ type: 'error', title: 'Grant failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevoke = async () => {
    setActionLoading(true);
    try {
      await api.exclusivity.revokeExclusivity(leadId);
      addToast({ type: 'success', title: 'Exclusivity revoked' });
      await onLeadUpdated?.();
    } catch (err) {
      addToast({ type: 'error', title: 'Revoke failed', message: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <Card className="p-5">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <SafeIcon icon={FiShield} className="text-primary" />
          Exclusivity
        </h3>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-gray-500">Status</span>
            <ExclusivityStatusBadge status={lead.exclusivity_status} />
          </div>

          {lead.exclusive_to && exclusiveHolderName && (
            <div className="text-sm text-gray-700">
              <span className="text-gray-500">Exclusive to </span>
              <span className="font-medium">{exclusiveHolderName}</span>
              {lead.exclusivity_expires_at && (
                <p className="text-xs text-gray-500 mt-0.5">
                  Expires {format(parseISO(lead.exclusivity_expires_at), 'MMM d, yyyy h:mm a')}
                </p>
              )}
            </div>
          )}

          {lead.split_applies && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-900">
              ⚠️ Split applies — 50/50 with original owner
              {lead.split_note && (
                <p className="text-xs text-yellow-800 mt-1">{lead.split_note}</p>
              )}
            </div>
          )}

          {canRequest && (
            <Button variant="outline" size="sm" className="w-full" onClick={() => setRequestOpen(true)}>
              Request Exclusivity
            </Button>
          )}

          {canManage && MANAGER_ROLES.has(viewer?.role) && (
            <div className="pt-2 border-t border-gray-100 space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Admin</p>
              <div className="flex gap-2">
                <Button
                  variant={isNonExclusive ? 'primary' : 'outline'}
                  size="sm"
                  className="flex-1"
                  disabled={actionLoading || isNonExclusive}
                  onClick={() => handleSetStatus('non_exclusive')}
                >
                  Non-Exclusive
                </Button>
                <Button
                  variant={!isNonExclusive ? 'primary' : 'outline'}
                  size="sm"
                  className="flex-1"
                  disabled={actionLoading || !isNonExclusive}
                  onClick={() => handleSetStatus('exclusive')}
                >
                  Exclusive
                </Button>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={actionLoading}
                onClick={openGrantModal}
              >
                Grant Exclusivity
              </Button>
              {lead.exclusive_to && (
                <Button
                  variant="danger"
                  size="sm"
                  className="w-full"
                  disabled={actionLoading}
                  onClick={handleRevoke}
                >
                  Revoke Exclusivity
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <Modal
        isOpen={requestOpen}
        onClose={() => !requestSubmitting && setRequestOpen(false)}
        title="Request Exclusivity"
        className="max-w-md"
      >
        <p className="text-sm text-gray-500 mb-3">
          Explain why you need exclusive access to this lead. A manager will review your request.
        </p>
        <textarea
          className="w-full border border-gray-200 rounded-md p-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent outline-none min-h-[100px] resize-y"
          placeholder="Reason for exclusivity request…"
          value={requestReason}
          onChange={(e) => setRequestReason(e.target.value)}
          disabled={requestSubmitting}
        />
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setRequestOpen(false)} disabled={requestSubmitting}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={handleRequestSubmit} disabled={requestSubmitting}>
            {requestSubmitting ? (
              <>
                <Spinner className="mr-2" />
                Submitting…
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={grantOpen}
        onClose={() => !actionLoading && setGrantOpen(false)}
        title="Grant Exclusivity"
        className="max-w-md"
      >
        {rosterLoading ? (
          <div className="flex items-center gap-2 py-6 text-gray-500 text-sm">
            <Spinner className="text-primary" />
            Loading team…
          </div>
        ) : (
          <div className="space-y-4">
            <Select
              label="Agent"
              value={grantUserId}
              onChange={(e) => setGrantUserId(e.target.value)}
              disabled={actionLoading}
              options={[
                { label: '— Select agent —', value: '' },
                ...roster.map((u) => ({
                  value: u.id,
                  label: u.full_name || 'Unknown',
                })),
              ]}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Timeframe (days)</label>
              <input
                type="number"
                min={1}
                className="w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm"
                value={grantDays}
                onChange={(e) => setGrantDays(e.target.value)}
                disabled={actionLoading}
              />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={() => setGrantOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" onClick={handleGrant} disabled={actionLoading || rosterLoading}>
            {actionLoading ? (
              <>
                <Spinner className="mr-2" />
                Granting…
              </>
            ) : (
              'Grant'
            )}
          </Button>
        </div>
      </Modal>
    </>
  );
}
